import Service from '@/Network/Services/Service'
import request from '@/Network/Requests/request'
import {
  DsPolicyItem,
  ListDsPolicyResponse,
  BlockedDoctorDsPolicyResponse,
  AuthorizedDoctorDsPolicyResponse,
  DsPolicyDetails,
  DsPolicyPatchDetails,
  GetCompanyDomainResponse,
  GetHealthUnitDomainResponse,
  GetPositionDomainResponse,
  GetScheduleDomainResponse,
  DoctorAuthorizationResponse
} from '@/Network/Types/Responses/DsPolicyResponses'
import {
  DsPolicySearchParams,
  CreateDsPolicyParams,
  UpdateDsPolicyParams,
  AddDoctorsToDsPolicyParams,
  UpdateDsPolicyLinksPayload
} from '@/Network/Types/Requests/DsPolicyRequests'

export default class PolicyService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/doctorStationPolicy'
  }

  async list(params: DsPolicySearchParams): Promise<ListDsPolicyResponse> {
    try {
      const response = await request.get<ListDsPolicyResponse>(this.getCompletePath(), params)
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as políticas de restrição.')
    }
  }

  async create(data: CreateDsPolicyParams): Promise<DsPolicyItem> {
    try {
      const response = await request.post<DsPolicyItem>(this.getCompletePath(), data)
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a política de restrição.')
    }
  }

  async patch(
    id: number,
    companyId: number,
    data: UpdateDsPolicyParams
  ): Promise<DsPolicyPatchDetails> {
    try {
      const response = await request.patch(
        this.getCompletePath(`/${id}/company/${companyId}`),
        data
      )
      return response.data as DsPolicyPatchDetails
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar as alterações da política de restrição.')
    }
  }

  async disableDsPolicy(id: number): Promise<DsPolicyPatchDetails> {
    try {
      const response = await request.put(this.getCompletePath(`/${id}/disable`), {})
      return response.data as DsPolicyPatchDetails
    } catch (e) {
      throw this.handleError(e, 'Não foi possível desativar a política de restrição.')
    }
  }

  async enableDsPolicy(id: number): Promise<DsPolicyPatchDetails> {
    try {
      const response = await request.put(this.getCompletePath(`/${id}/activate`), {})
      return response.data as DsPolicyPatchDetails
    } catch (e) {
      throw this.handleError(e, 'Não foi possível ativar a política de restrição.')
    }
  }

  async duplicateDsPolicy(
    id: number,
    data: { newPolicyName: string; targetCompanyId: number }
  ): Promise<DsPolicyItem> {
    try {
      const response = await request.post<DsPolicyItem>(
        this.getCompletePath(`/${id}/duplicate`),
        data
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível duplicar a política de restrição.')
    }
  }

  async getById(id: number): Promise<DsPolicyDetails> {
    try {
      const response = await request.get<DsPolicyDetails>(this.getCompletePath(`/${id}`))
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes da política de restrição.')
    }
  }

  async addAuthorizedDoctors(dsPolicyId: number, data: AddDoctorsToDsPolicyParams): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/${dsPolicyId}/authorized`), data)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível adicionar os médicos autorizados à política de restrição.'
      )
    }
  }

  async addBlockedDoctors(
    dsPolicyId: number,
    payload: { dsPolicyId: number; doctorIds: number[] }
  ): Promise<void> {
    try {
      await request.post(this.getCompletePath(`/${dsPolicyId}/blocked`), payload)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível adicionar os médicos bloqueados à política de restrição.'
      )
    }
  }

  async deleteBlockedDoctor(dsPolicyBlockedId: number): Promise<void> {
    try {
      await request.delete(this.getCompletePath(`/blocked/${dsPolicyBlockedId}/delete`))
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível remover o médico bloqueado da política de restrição.'
      )
    }
  }

  async deleteAuthorizedDoctor(dsPolicyAuthorizedId: number): Promise<void> {
    try {
      await request.delete(this.getCompletePath(`/authorized/${dsPolicyAuthorizedId}/delete`))
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível remover o médico autorizado da política de restrição.'
      )
    }
  }

  async listAuthorizedDoctors(
    dsPolicyId: number,
    params: DsPolicySearchParams
  ): Promise<AuthorizedDoctorDsPolicyResponse> {
    try {
      const response = await request.get<AuthorizedDoctorDsPolicyResponse>(
        this.getCompletePath(`/${dsPolicyId}/authorized/list`),
        params
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos autorizados da política de restrição.'
      )
    }
  }

  async listBlockedDoctors(
    dsPolicyId: number,
    params: DsPolicySearchParams
  ): Promise<BlockedDoctorDsPolicyResponse> {
    try {
      const response = await request.get<BlockedDoctorDsPolicyResponse>(
        this.getCompletePath(`/${dsPolicyId}/blocked/list`),
        params
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos bloqueados da política de restrição.'
      )
    }
  }

  async listAuthorizedDoctorsByPolicy(
    dsPolicyId: number
  ): Promise<AuthorizedDoctorDsPolicyResponse> {
    try {
      const response = await request.get<AuthorizedDoctorDsPolicyResponse>(
        this.getCompletePath(`/${dsPolicyId}/authorized/list`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos autorizados da política de restrição.'
      )
    }
  }

  async listBlockedDoctorsByPolicy(dsPolicyId: number): Promise<BlockedDoctorDsPolicyResponse> {
    try {
      const response = await request.get<BlockedDoctorDsPolicyResponse>(
        this.getCompletePath(`/${dsPolicyId}/blocked/list`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar os médicos bloqueados da política de restrição.'
      )
    }
  }

  async getCompanyDomain(dsPolicyId: number): Promise<GetCompanyDomainResponse> {
    try {
      const response = await request.get<GetCompanyDomainResponse>(
        this.getCompletePath(`/${dsPolicyId}/companyDomain`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as empresas vinculadas à política de restrição.'
      )
    }
  }

  async getHealthUnitDomain(dsPolicyId: number): Promise<GetHealthUnitDomainResponse> {
    try {
      const response = await request.get<GetHealthUnitDomainResponse>(
        this.getCompletePath(`/${dsPolicyId}/healthUnitDomain`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as unidades vinculadas à política de restrição.'
      )
    }
  }

  async getPositionDomain(dsPolicyId: number): Promise<GetPositionDomainResponse> {
    try {
      const response = await request.get<any>(this.getCompletePath(`/${dsPolicyId}/positionDomain`))
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as posições vinculadas à política de restrição.'
      )
    }
  }

  async getScheduleDomain(dsPolicyId: number): Promise<GetScheduleDomainResponse> {
    try {
      const response = await request.get<any>(this.getCompletePath(`/${dsPolicyId}/scheduleDomain`))
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as escalas vinculadas à política de restrição.'
      )
    }
  }

  async listCompanyDsPolicyLinks(companyId: number): Promise<GetCompanyDomainResponse> {
    try {
      const response = await request.get<GetCompanyDomainResponse>(
        this.getCompletePath(`/company/${companyId}`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as políticas de restrição vinculadas à empresa.'
      )
    }
  }

  async listHealthUnitDsPolicyLinks(healthUnitId: number): Promise<GetHealthUnitDomainResponse> {
    try {
      const response = await request.get<GetHealthUnitDomainResponse>(
        this.getCompletePath(`/healthUnit/${healthUnitId}`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as políticas de restrição vinculadas à unidade.'
      )
    }
  }

  async listPositionDsPolicyLinks(positionId: number): Promise<GetPositionDomainResponse> {
    try {
      const response = await request.get<GetPositionDomainResponse>(
        this.getCompletePath(`/position/${positionId}`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as políticas de restrição vinculadas à posição.'
      )
    }
  }

  async listScheduleDsPolicyLinks(scheduleId: number): Promise<GetScheduleDomainResponse> {
    try {
      const response = await request.get<GetScheduleDomainResponse>(
        this.getCompletePath(`/schedule/${scheduleId}`)
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível carregar as políticas de restrição vinculadas à escala.'
      )
    }
  }

  async updateCompanyDsPolicyLinks(
    dsPolicyCompanyId: number,
    data: UpdateDsPolicyLinksPayload
  ): Promise<void> {
    try {
      await request.patch(this.getCompletePath(`/company/${dsPolicyCompanyId}/policy-links`), data)
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar as políticas de restrição vinculadas à empresa.'
      )
    }
  }

  async updateHealthUnitDsPolicyLinks(
    dsPolicyHealthUnitId: number,
    data: UpdateDsPolicyLinksPayload
  ): Promise<void> {
    try {
      await request.patch(
        this.getCompletePath(`/healthUnit/${dsPolicyHealthUnitId}/policy-links`),
        data
      )
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar as políticas de restrição vinculadas à unidade.'
      )
    }
  }

  async updateScheduleDsPolicyLinks(
    dsPolicyScheduleId: number,
    data: UpdateDsPolicyLinksPayload
  ): Promise<void> {
    try {
      await request.patch(
        this.getCompletePath(`/schedule/${dsPolicyScheduleId}/policy-links`),
        data
      )
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar as políticas de restrição vinculadas à escala.'
      )
    }
  }

  async updatePositionDsPolicyLinks(
    dsPolicyPositionId: number,
    data: UpdateDsPolicyLinksPayload
  ): Promise<void> {
    try {
      await request.patch(
        this.getCompletePath(`/position/${dsPolicyPositionId}/policy-links`),
        data
      )
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível salvar as políticas de restrição vinculadas à posição.'
      )
    }
  }

  async promoteAuthorizedDoctor(policyAuthorizationId: number): Promise<void> {
    try {
      const response = await request.put<any>(
        this.getCompletePath(`/promoteDoctor/${policyAuthorizationId}`),
        {}
      )
      return response.data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível promover o médico na política de restrição.')
    }
  }

  async checkDoctorAuthorization(
    doctorStationId: number,
    doctorId: number
  ): Promise<DoctorAuthorizationResponse> {
    try {
      const queryString = `?doctorStationId=${doctorStationId}&doctorId=${doctorId}`
      const response = await request.get<DoctorAuthorizationResponse>(
        this.getCompletePath(`/checkDoctorAuthorization${queryString}`)
      )

      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        'Não foi possível verificar se o médico está autorizado no plantão.'
      )
    }
  }
}
